/*
 * Babyset cart guard (window.BabysetCart) – keeps the gift for mum ("gumica za mamu") honest in the cart.
 *
 * Loaded by the builder (sections/babyset-builder.liquid) and, on every page whose cart holds babyset lines or the
 * cart attribute "Poklon za mamu", by snippets/babyset-cart-guard.liquid (rendered once from sections/header.liquid).
 *
 * A set = the cart lines with the same hidden property _babyset_id. The builder puts the gift of a set
 * ("Poklon za mamu: Gumica – Mint") on one line only (the dekica), together with the hidden threshold
 * _babyset_prag in cents (8000 = "više od 80 €"). sync():
 * - a set whose value (sum of its lines before discounts) is no longer above its threshold – e.g. the customer
 *   removed pieces in the cart – loses the gift property (POST /cart/change.js with the line's other properties).
 *   The line is addressed by its KEY, never by its position: Dawn's cart stays clickable while this runs, and a
 *   position read from an older cart can already belong to another line (another set's personalised piece).
 *   A key that is gone (the customer changed or removed that line meanwhile) just means: read the cart again;
 * - the cart attribute "Poklon za mamu" is set to the gifts of the sets that still have one ("Gumica – Mint"),
 *   or emptied (Shopify then removes it) – so an old colour never stays on the order.
 * Since 29. 9. 2026 the customer no longer picks a colour: the gift reads "Gumica S (boju bira Šigureca)" (the
 * section's gift name) – any text works here, the sync only copies it (two sets: "Gumica S (boju bira Šigureca),
 * Gumica S (boju bira Šigureca)").
 * It never adds a gift. It runs on load (guard snippet only) and after every Dawn cart update (pubsub
 * "cart-update"); when it changed a line it asks Dawn's cart page to re-render (publish with source
 * "babyset-cart", which it ignores itself).
 */
(() => {
  const GIFT = 'Poklon za mamu';
  const THRESHOLD = '_babyset_prag';
  const SET_ID = '_babyset_id';
  const SOURCE = 'babyset-cart';
  const MAX_CHANGES = 20;

  const script = document.currentScript;
  const autoStart = Boolean(script && script.hasAttribute('data-babyset-cart-auto'));

  if (window.BabysetCart) {
    if (autoStart) window.BabysetCart.start();
    return;
  }

  const routes = window.routes || {};
  const endpoint = (route, fallback) => `${route || fallback}.js`;
  const CART = endpoint(routes.cart_url, '/cart');
  const CHANGE = endpoint(routes.cart_change_url, '/cart/change');
  const UPDATE = endpoint(routes.cart_update_url, '/cart/update');
  const JSON_HEADERS = { 'Content-Type': 'application/json', Accept: 'application/json' };

  async function getCart() {
    const response = await fetch(CART, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
    if (!response.ok) throw new Error(`cart ${response.status}`);
    return response.json();
  }

  /** The babyset sets of a cart: [{ id, value, lines: [{ item }] }] (item.key addresses the line in change.js). */
  function setsOf(cart) {
    const sets = new Map();
    (cart.items || []).forEach((item) => {
      const id = item.properties ? item.properties[SET_ID] : null;
      if (!id) return;
      if (!sets.has(id)) sets.set(id, { id, value: 0, lines: [] });
      const set = sets.get(id);
      const price = item.original_line_price != null ? item.original_line_price : item.line_price;
      set.value += Number(price) || 0;
      set.lines.push({ item });
    });
    return [...sets.values()];
  }

  /** The threshold of a set in cents, from the line that carries its gift (null when unknown). */
  function thresholdOf(set) {
    for (const { item } of set.lines) {
      const raw = item.properties ? item.properties[THRESHOLD] : null;
      const value = Number(raw);
      if (raw != null && raw !== '' && Number.isFinite(value) && value >= 0) return value;
    }
    return null;
  }

  function giftLines(set) {
    return set.lines.filter(({ item }) => item.properties && item.properties[GIFT]);
  }

  /** The first line whose set is not above its threshold any more but still carries the gift. */
  function firstStale(cart) {
    for (const set of setsOf(cart)) {
      const threshold = thresholdOf(set);
      if (threshold == null) continue;
      if (set.value > threshold) continue;
      const [stale] = giftLines(set);
      if (stale) return stale;
    }
    return null;
  }

  /** "Gumica – Mint" (one set) / "Gumica – Mint, Gumica – Cacao" (two sets) / "" (none). */
  function giftsText(cart) {
    const gifts = [];
    setsOf(cart).forEach((set) => {
      const threshold = thresholdOf(set);
      if (threshold != null && set.value <= threshold) return;
      const [carrier] = giftLines(set);
      if (carrier) gifts.push(String(carrier.item.properties[GIFT]));
    });
    return gifts.join(', ');
  }

  /** Remove the gift from ONE line, addressed by its key; null when that line is no longer in the cart. */
  async function removeGift({ item }) {
    const properties = { ...(item.properties || {}) };
    delete properties[GIFT];
    delete properties[THRESHOLD];
    const response = await fetch(CHANGE, {
      method: 'POST',
      headers: JSON_HEADERS,
      credentials: 'same-origin',
      body: JSON.stringify({ id: item.key, quantity: item.quantity, properties }),
    });
    // 400/404: the key is gone – the customer removed or changed that line in the meantime.
    if (response.status === 400 || response.status === 404) return null;
    if (!response.ok) throw new Error(`change ${response.status}`);
    return response.json();
  }

  async function writeAttribute(value) {
    const response = await fetch(UPDATE, {
      method: 'POST',
      headers: JSON_HEADERS,
      credentials: 'same-origin',
      body: JSON.stringify({ attributes: { [GIFT]: value } }),
    });
    if (!response.ok) throw new Error(`update ${response.status}`);
    return response.json();
  }

  let running = null;
  let again = false;

  async function run() {
    let cart = await getCart();
    let changedLines = false;
    for (let i = 0; i < MAX_CHANGES; i += 1) {
      const stale = firstStale(cart);
      if (!stale) break;
      const next = await removeGift(stale);
      if (next) changedLines = true;
      // a line that vanished meanwhile: decide again from the cart as it is now
      cart = next || (await getCart());
    }
    const wanted = giftsText(cart);
    const current = (cart.attributes && cart.attributes[GIFT]) || '';
    if (wanted !== current) await writeAttribute(wanted);
    return { changedLines, gifts: wanted, attributeChanged: wanted !== current };
  }

  /** One sync at a time; a request while one runs makes it run once more afterwards. */
  async function sync() {
    if (running) {
      again = true;
      return running;
    }
    running = (async () => {
      let result = { changedLines: false, gifts: '', attributeChanged: false };
      do {
        again = false;
        const next = await run();
        result = {
          changedLines: result.changedLines || next.changedLines,
          gifts: next.gifts,
          attributeChanged: result.attributeChanged || next.attributeChanged,
        };
      } while (again);
      return result;
    })();
    try {
      return await running;
    } finally {
      running = null;
    }
  }

  /** Dawn's cart page / drawer show the line properties: re-render them after a change. */
  function refreshDawnCart(result) {
    if (!result || !result.changedLines) return;
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: SOURCE });
    }
  }

  function syncAndRefresh() {
    return sync()
      .then((result) => {
        refreshDawnCart(result);
        return result;
      })
      .catch(() => null);
  }

  let started = false;
  let timer = null;

  function start() {
    if (started) return;
    started = true;
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event && event.source === SOURCE) return;
        window.clearTimeout(timer);
        timer = window.setTimeout(syncAndRefresh, 150);
      });
    }
    syncAndRefresh();
  }

  window.BabysetCart = { sync, start, GIFT, THRESHOLD, SET_ID };
  if (autoStart) start();
})();
